import React, { useState, useEffect, useRef } from 'react';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { LexRuntimeV2Client, RecognizeTextCommand } from '@aws-sdk/client-lex-runtime-v2';

// Use environment variables directly
const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
const identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID || '';
const botId = process.env.REACT_APP_LEX_BOT_ID || '';
const botAliasId = process.env.REACT_APP_LEX_BOT_ALIAS_ID || '';
const localeId = process.env.REACT_APP_LEX_LOCALE_ID || 'en_US';

/**
 * LexTester component for testing direct Lex integration with text input
 * This helps isolate voice-specific issues from general Lex connectivity issues
 */
const LexTester: React.FC = () => {
  const [input, setInput] = useState('');
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const lexClientRef = useRef<LexRuntimeV2Client | null>(null);
  
  // Initialize the Lex client
  useEffect(() => {
    const initLexClient = async () => {
      try {
        console.log('Initializing Lex client for text testing...');
        console.log('Using Identity Pool ID:', identityPoolId);
        
        // Set up Cognito Identity credentials
        const cognitoIdentityClient = new CognitoIdentityClient({
          region
        });
        
        const credentials = fromCognitoIdentityPool({
          client: cognitoIdentityClient,
          identityPoolId
        });
        
        // Create Lex client
        lexClientRef.current = new LexRuntimeV2Client({
          region,
          credentials,
          maxAttempts: 3,
          retryMode: 'standard'
        });
        
        console.log('Lex client initialized successfully');
        setIsInitialized(true);
      } catch (err) {
        console.error('Error initializing Lex client:', err);
        setError('Failed to initialize Lex client');
      }
    };
    
    initLexClient();
  }, []);
  
  // Send text to Lex
  const sendToLex = async () => {
    if (!lexClientRef.current) {
      setError('Lex client not initialized');
      return;
    }
    
    if (!input.trim()) {
      setError('Please enter some text');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      console.log('Sending text to Lex:', input);
      console.log('Bot ID:', botId);
      console.log('Bot Alias ID:', botAliasId);
      
      // Create a session ID
      const sessionId = `session-${Date.now()}`;
      
      // Create the command
      const command = new RecognizeTextCommand({
        botId,
        botAliasId,
        localeId,
        sessionId: sessionId,
        text: input
      });
      
      // Send the command
      const response = await lexClientRef.current.send(command);
      console.log('Received response from Lex:', response);
      
      setResponse(response);
    } catch (err) {
      console.error('Error sending text to Lex:', err);
      if (err instanceof Error) {
        setError(`Error: ${err.message}`);
      } else {
        setError('Unknown error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="lex-tester" style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2>Lex Text Integration Tester</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="text-input" style={{ display: 'block', marginBottom: '5px' }}>
            Enter text to send to Lex:
          </label>
          <div style={{ display: 'flex' }}>
            <input
              id="text-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!isInitialized || isLoading}
              placeholder="Type a command like 'Turn on the lights'"
              style={{ 
                flex: 1, 
                padding: '8px', 
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
              onKeyPress={(e) => e.key === 'Enter' && sendToLex()}
            />
            <button
              onClick={sendToLex}
              disabled={!isInitialized || isLoading}
              style={{
                marginLeft: '10px',
                padding: '8px 16px',
                backgroundColor: isLoading ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
        
        {error && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24',
            borderRadius: '4px',
            marginTop: '10px'
          }}>
            {error}
          </div>
        )}
        
        {!isInitialized && !error && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#e2e3e5', 
            color: '#383d41',
            borderRadius: '4px',
            marginTop: '10px'
          }}>
            Initializing Lex client...
          </div>
        )}
        
        {response && (
          <div style={{ marginTop: '20px' }}>
            <h3>Response:</h3>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '15px',
              borderRadius: '4px',
              border: '1px solid #dee2e6',
              overflowX: 'auto'
            }}>
              {response.messages && response.messages.length > 0 ? (
                <div>
                  <h4>Messages:</h4>
                  {response.messages.map((msg: any, index: number) => (
                    <div key={index} style={{ marginBottom: '10px' }}>
                      <strong>Content:</strong> {msg.content}<br />
                      <strong>Type:</strong> {msg.contentType}
                    </div>
                  ))}
                </div>
              ) : (
                <p>No messages in response</p>
              )}
              
              {response.sessionState && (
                <div style={{ marginTop: '15px' }}>
                  <h4>Session State:</h4>
                  <pre style={{ margin: 0 }}>
                    {JSON.stringify(response.sessionState, null, 2)}
                  </pre>
                </div>
              )}
              
              {response.inputTranscript && (
                <div style={{ marginTop: '15px' }}>
                  <h4>Input Transcript:</h4>
                  <p>{response.inputTranscript}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div style={{ marginTop: '30px' }}>
        <h3>Configuration:</h3>
        <pre style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '15px',
          borderRadius: '4px',
          border: '1px solid #dee2e6'
        }}>
{`Region: ${region}
Identity Pool ID: ${identityPoolId}
Bot ID: ${botId}
Bot Alias ID: ${botAliasId}
Locale ID: ${localeId}`}
        </pre>
      </div>
    </div>
  );
};

export default LexTester;